/*****************************************************************************
 *   © Copyright 2021   Quantitative Software Management, Inc.               *
 *              --  All rights reserved.  --                                 *
 *   Unpublished -- rights reserved under the Copyright Laws                 *
 *   of the United States.                                                   *
 *                                                                           *
 *   This computer software is protected by trade secret, copyright law,     *
 *   and international treaties and contains proprietary and confidential    *
 *   trade secrets of Quantitative Software Management, Inc.  Unauthorized   *
 *   use, reproduction, or distribution of this program or any portion of    *
 *   it will result in severe civil or criminal penalties and will be        *
 *   prosecuted to the full extent of the law.                               *
 *                                                                           *
 *   Use, duplication, or disclosure by the Government is subject to         *
 *   restrictions as set forth in                                            *
 *      (i) a license agreement incorporated in the contract,                *
 *      (ii) the definition of "restricted rights" in paragraph (c)(1)(ii)   *
 *           of DFARS 252.227-7013 (1988), and                               *
 *      (iii) the definition of "restricted rights" in paragraph (a) of      *
 *            FAR 52.227-14, Alt III (Jun 1987) or                           *
 *      (iv) FAR 52.227-19 (Jun 1987), as applicable.                        *
 *   For purposes of the FAR, this software shall be deemed to               *
 *   be "unpublished" and licensed with disclosure prohibitions.             *
 *   Contractor/Manufacturer is   Quantitative Software Management, Inc.     *
 *                                                                           *
 *****************************************************************************/

import { Injectable, Inject } from '@angular/core';
import { Observable, of as observableOf } from 'rxjs';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { DOCUMENT } from '@angular/common';
import { Router } from '@angular/router';
import { LoadingService } from './loading.service';
import { AppConstants } from '../app.constants';
import { Title } from '@angular/platform-browser';

export enum ApplicationType {
  Authority = 'Authority',  //used inUtilityService. _appUrlNames to hold the authority section of the url
  Web = 'Web',
  Admin = 'Admin',
  Collab = 'Collab'
}

@Injectable({
  providedIn: 'root'
})
export class BaseUtilityService {
  constructor(public http: HttpClient,
    @Inject(DOCUMENT) protected document: any, public router: Router, public loadingService: LoadingService,
    public titleService: Title) { }

  public debuglog(level: number, message?: any, ...optionalParams: any[]): void {
    if (level >= 0) {
      console.log(message, ...optionalParams);
    }
  }

  public logPerformance(entryType: string = 'markAndMeasure'): void {
    let entries: PerformanceEntryList = entryType == 'markAndMeasure' ? performance.getEntries() : performance.getEntriesByType(entryType);
    let columns: string[] = [];
    if (entryType == 'markAndMeasure') {
      entries = entries.filter(x => (x.entryType == 'mark' || x.entryType == 'measure') && !x.name.startsWith('Zone'));
      columns = ['name', 'entryType', 'startTime', 'duration'];
    }
    console.table(entries, columns);
    console.table(this.performanceData);
  }

  public measurePerformance(title: string): void {
    performance.mark(title);
    performance.measure(title);
    performance.now();
    let data = performance.toJSON();
    this.performanceData.push({ 'Entry': title, 'Origin': new Date(data.timeOrigin), 'Load End': new Date( data.timing.loadEventEnd) });
  }

  private performanceData: any[] = []; 

  public startTimer() {
    this.performanceData.push({ 'TimeStamp': Date.now() });
  }

  public endTimer() {
    console.log('duration = ', Date.now() - this.performanceData[0]?.TimeStamp);
    setTimeout(() => this.performanceData = [], 1000);
  }


  //URL utitlities
  public Initialized: boolean = false;
  private _appUrlNames: Map<ApplicationType, string> = new Map<ApplicationType, string>();
  public AppUrlNames(): Observable<Map<ApplicationType, string>> {
    let theAppNames: Observable<Map<ApplicationType, string>>;
    if (this.Initialized) {
      theAppNames = observableOf(this._appUrlNames);
    }
    else {
      theAppNames = new Observable<Map<ApplicationType, string>>(observer => {
        let rawUrl = 'api/utilities/appUrlNames';
        this.http.get<string>(rawUrl, this.StringReturnOptions(this.AjaxOptions())).subscribe(st => {
          let urlNames = st.split(" ");
          for (let inx = 0; inx < urlNames.length; inx++) {
            let urlName = urlNames[inx].split("::");
            this._appUrlNames.set(urlName[0] as ApplicationType, urlName[1]);
          }
          this.Initialized = true;

          observer.next(this._appUrlNames);
          observer.complete();
        });
      });
    }
    return theAppNames;
  }

  //Use ServerAppUrl instead---can save trip to server since the "qsmxxx" segments are cached in the client
  public RetrieveServerAppUrl(appName: string, page: string): Observable<string> {
    let rawUrl = `api/utilities/serverUrl?AppName=${appName}&Page=${page}`;
    return this.http.get<string>(encodeURI(rawUrl), this.StringReturnOptions(this.AjaxOptions()));
  }

  public RelativeUrl(urlString: string, includeApp: boolean = true): string {
    let relativeUrl = null;
    if (urlString) {
      if (urlString.startsWith("/")) {
        relativeUrl = urlString;
      }
      else  if (urlString !==AppConstants.BLANK_PAGE) {
        try {
          let fullUrl: URL = new URL(urlString);
          relativeUrl = fullUrl.pathname + fullUrl.search;
          if (!includeApp) {
            let path = relativeUrl.split("/", 2);
            //relativeUrl starts with /, so path[0]="", path[1]=appname, path[2] is what we want
            if (path.length === 2) {
              relativeUrl = relativeUrl.substring(path[1].length + 1); //include the leading /
            }
            else {
              relativeUrl = "/"
            }
          }
        }
        catch {
          //console.log("RelativeURL invalid url:", urlString);
        }
      }
    }
    return relativeUrl;
  }

  public ServerAppUrl(appName: ApplicationType, page: string): Observable<string> {
    if (appName === ApplicationType.Authority) {
     throw new RangeError("Invalid Application Type");
    }
    let stringObservable = new Observable<string>(observer => {
      this.AppUrlNames().subscribe(urlNames => {
        let authority = urlNames.get(ApplicationType.Authority);
        let appUrlName = urlNames.get(appName);
        let pageSlash = "/";
        if (page.charAt(0) === '/') {
          pageSlash = "";
        }
        let url: string = `${authority}/${appUrlName}${pageSlash}${page}`;
        observer.next(url);
        observer.complete();
      });
    });
    return stringObservable as Observable<string>;
  }

  //No longer used--this terminates the angular app
  public RedirectToApp(app: ApplicationType, page: string): void {
    this.ServerAppUrl(app, page).subscribe(url => {
      if (url !== "") {
        this.RedirectToUrl(url);
      }
    });
  }

  //No longer used--this terminates the angular app
  public RedirectToUrl(url: string): void {
    this.document.location.href = url;
  }

  public NavigateToAspx(aspxUrl: string): void {
    this.loadingService.ShowLoading.next(true);
    aspxUrl = this.RelativeUrl(aspxUrl);
    this.router.navigate(['/page'], { queryParams: { aspxpath: aspxUrl } });
  }

  public NavigateToLogin() {
    this.loadingService.ShowLoading.next(true);
    let theUrl = this.RelativeUrl(this.router.url);
    this.router.navigate(['/login'], { queryParams: { returnUrl: theUrl } });
  }

  //Popups
  public popupChecks: Set<(url: string, previousUrl: string) => boolean> = new Set<(url: string, previousUrl: string) => boolean>();
  public shouldClosePopups(url: string, previousUrl: string): boolean {
    let shouldClose = true;
    for (let check of this.popupChecks) {
      shouldClose = shouldClose && check(url, previousUrl);
      if (!shouldClose) {
        break;
      }
    };
    return shouldClose;
  }

    //Error Handling
  public ErrorMessage(msg: string): string {
    return "An Error Occurred: " + msg + this.CurrentTimeString();
  }

  public CurrentTimeString(): string {
    var date = new Date();
    return date.toString() + " " + date.getMilliseconds();
  }
  //Http Utilities
  public AjaxOptions(): { headers: HttpHeaders; } {
    return { headers: new HttpHeaders({ 'X-Requested-With': 'XMLHttpRequest' }) };
  }

  public JsonOptions(): { headers: HttpHeaders; } {
    let options = this.AjaxOptions();
    let newHeaders = options.headers.set('Content-Type', 'application/json');
    return { headers: newHeaders };
  }

  public StringReturnOptions(options: {
    headers?: HttpHeaders | {
      [header: string]: string | string[];
    };
    observe?: 'body';
    params?: HttpParams | {
      [param: string]: string | string[];
    };
    reportProgress?: boolean;
    responseType?: 'json';
    withCredentials?: boolean;
  }): {
      headers?: HttpHeaders | {
        [header: string]: string | string[];
      };
      observe?: 'body';
      params?: HttpParams | {
        [param: string]: string | string[];
      };
      reportProgress?: boolean;
      responseType?: 'json';
      withCredentials?: boolean;
    } {
    return { ...options, responseType: 'text' as 'json' };
  }

  public browserIsFirefox(): boolean {
    return window.navigator.userAgent.toLowerCase().indexOf('firefox') > -1;
  }

  public browserIsSafari(): boolean {
  let ua = window.navigator.userAgent.toLowerCase();
    return ua.indexOf('macintosh') > -1 && ua.indexOf('safari') > -1;
  }
}
